import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  CircularProgress,
  Box,
  Button,
  Typography,
  TextField,
  Autocomplete,
  Grid,
  Stack,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { StyledButton } from "components/StyledComponents/styledComponents";

export default function DmaPayoutsDialog({
  open,
  onClose,
  startDate,
  endDate,
  data = [],
  loading = false,
  error = null,
}) {
  const [filteredData, setFilteredData] = useState([]);
  const [searchColumn, setSearchColumn] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
  const [columnOptions, setColumnOptions] = useState([]);
  const gridRef = useRef();

  // Format currency for Indian numbering system
  const formatIndianCurrency = (value) => {
    if (value == null || value === "") return "";
    return Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "dd-MMM-yyyy");
    } catch {
      return dateString;
    }
  };

  // Define column definitions for AG Grid
  const columnDefs = useMemo(
    () => [
      {
        headerName: "S.No",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 80,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Transaction Date",
        field: "tranDate",
        sortable: true,
        filter: true,
        width: 140,
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        headerName: "Invoice No",
        field: "invoiceNo",
        sortable: true,
        filter: true,
        width: 120,
      },
      {
        headerName: "DSA Name",
        field: "dsaname",
        sortable: true,
        filter: true,
        width: 150,
      },
      {
        headerName: "Remarks",
        field: "allRemarks",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 200,
      },
      {
        headerName: "Voucher No",
        field: "vouchers",
        sortable: true,
        filter: true,
        width: 150,
      },
      {
        headerName: "Amount",
        field: "totalInvoiceAmount",
        sortable: true,
        filter: true,
        width: 150,
        valueFormatter: (params) => formatIndianCurrency(params.value),
        cellStyle: (params) => ({
          fontWeight: "bold",
          textAlign: "right",
          color: "#2e7d32", // Green color for positive amounts
        }),
        type: "numericColumn",
      },
    ],
    []
  );

  // Initialize column options for search
  useEffect(() => {
    const options = columnDefs
      .filter((col) => col.field)
      .map((col) => ({
        label: col.headerName,
        value: col.field,
        field: col.field,
        type: col.type || "string",
      }));
    setColumnOptions(options);

    // Auto-select the first column by default
    if (options.length > 0 && !searchColumn) {
      setSearchColumn(options[0]);
    }
  }, [columnDefs]);

  // Initialize data
  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  // Filter data based on search
  useEffect(() => {
    if (!searchText.trim() || !searchColumn) {
      setFilteredData(data);
      return;
    }

    const lowerSearchText = searchText.toLowerCase();
    const filtered = data.filter((row) => {
      const fieldValue = row[searchColumn.field];
      if (fieldValue == null || fieldValue === undefined) return false;

      // Handle numeric fields differently
      if (searchColumn.type === "numericColumn" || typeof fieldValue === "number") {
        return fieldValue.toString().includes(searchText);
      }

      return fieldValue.toString().toLowerCase().includes(lowerSearchText);
    });

    setFilteredData(filtered);
  }, [data, searchColumn, searchText]);

  // Calculate footer totals for filtered rows
  const calculateFooterTotals = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return {
        totalInvoiceAmount: 0,
        count: 0,
      };
    }

    return {
      totalInvoiceAmount: filteredData.reduce(
        (sum, row) => sum + (parseFloat(row.totalInvoiceAmount) || 0),
        0
      ),
      count: filteredData.length,
    };
  }, [filteredData]);

  // Update footer when data or filter changes
  useEffect(() => {
    const totals = calculateFooterTotals;

    const footerRow = {
      tranDate: "",
      invoiceNo: "",
      dsaname: "",
      allRemarks: searchText && searchColumn ? "Total (Filtered)" : "Grand Total",
      vouchers: "",
      totalInvoiceAmount: totals.totalInvoiceAmount,
      _remarks: `${totals.count} entries`,
    };

    setPinnedBottomRowData([footerRow]);
  }, [filteredData, searchText, searchColumn, calculateFooterTotals]);

  // Handle search input changes
  const handleSearchColumnChange = (event, newValue) => {
    setSearchColumn(newValue);
    if (!newValue) {
      setSearchText("");
    }
  };

  const handleSearchTextChange = (event) => {
    setSearchText(event.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchColumn(columnOptions[0] || null);
    setSearchText("");
  };

  // Handle Excel download
  const handleDownloadExcel = () => {
    if (!filteredData || filteredData.length === 0) return;

    // Prepare all data including footer
    const allData = [...filteredData, ...pinnedBottomRowData];

    // Re-map to readable column structure
    const exportData = allData.map((row, index) => ({
      "S.No": index + 1,
      "Transaction Date": formatDate(row.tranDate),
      "Invoice No": row.invoiceNo || "",
      "DSA Name": row.dsaname || "",
      Remarks: row.allRemarks || "",
      "Voucher No": row.vouchers || "",
      Amount: row.totalInvoiceAmount || 0,
      "": row._remarks || "", // For footer count
    }));

    // Create worksheet & workbook
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "DMA Payouts Breakdown");

    // Auto column width
    const maxWidths = [];
    exportData.forEach((row) => {
      Object.values(row).forEach((val, i) => {
        const len = val ? val.toString().length : 10;
        maxWidths[i] = Math.max(maxWidths[i] || 10, len);
      });
    });
    worksheet["!cols"] = maxWidths.map((w) => ({ wch: w + 2 }));

    // Write to file
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `DMA_Payouts_Breakdown_${startDate}_to_${endDate}.xlsx`
    );
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "#f4f6f8",
        zIndex: 1300,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* ── Top Bar ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          backgroundColor: "#fff",
          borderBottom: "1px solid #e0e0e0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          flexShrink: 0,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            color: "#1F2937", // dark text — fixes your visibility issue
            borderColor: "#1F2937",
            "&:hover": {
              backgroundColor: "#f3f4f6",
              borderColor: "#111827",
            },
          }}
        >
          Back
        </Button>

        <Typography variant="h6" fontWeight={700} color="#1F2937">
          DMA Payouts Breakdown
        </Typography>

        <StyledButton
          variant="contained"
          color="primary"
          onClick={handleDownloadExcel}
          disabled={filteredData.length === 0}
          sx={{ textTransform: "none" }}
        >
          Download Excel
        </StyledButton>
      </Box>

      {/* ── Filter Section ── */}
      <Box sx={{ px: 3, pt: 2, pb: 1, flexShrink: 0 }}>
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            p: 2,
            backgroundColor: "#fff",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle2" fontWeight={600} color="#333">
              Filters
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={handleClearSearch}
              disabled={!searchText}
              sx={{ textTransform: "none", fontSize: 12 }}
            >
              Clear Search
            </Button>
          </Stack>

          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={columnOptions}
                getOptionLabel={(option) => option.label}
                value={searchColumn}
                onChange={handleSearchColumnChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Column"
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                )}
                disableClearable={false}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                label={searchColumn ? `Search in "${searchColumn.label}"` : "Search value"}
                variant="outlined"
                size="small"
                value={searchText}
                onChange={handleSearchTextChange}
                disabled={!searchColumn}
                placeholder={
                  searchColumn ? `Type to filter ${searchColumn.label}...` : "Select a column first"
                }
              />
            </Grid>
          </Grid>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.2, display: "block" }}>
            {searchText && searchColumn ? (
              <>
                Showing <strong>{filteredData.length}</strong> of <strong>{data.length}</strong>{" "}
                rows &nbsp;|&nbsp;
                <span style={{ color: "#1976d2" }}>
                  {searchColumn.label} = &quot;{searchText}&quot;
                </span>
              </>
            ) : (
              <>
                Total <strong>{data.length}</strong> rows
              </>
            )}
          </Typography>
        </Box>
      </Box>

      {/* ── AG Grid ── */}
      <Box
        sx={{
          flex: 1,
          px: 3,
          pb: 3,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {loading ? (
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <div
            className="ag-theme-alpine"
            style={{ flex: 1, width: "100%", height: "100%", minHeight: 0 }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              pinnedBottomRowData={pinnedBottomRowData}
              defaultColDef={{
                resizable: true,
                filter: true,
                sortable: true,
                wrapText: true,
                autoHeight: true,
                cellStyle: {
                  whiteSpace: "normal",
                  lineHeight: "1.4",
                  paddingTop: "6px",
                  paddingBottom: "6px",
                },
              }}
              getRowStyle={(params) => {
                if (params.node.rowPinned === "bottom") {
                  return { fontWeight: "bold", backgroundColor: "#f5f6f8" };
                }
                return null;
              }}
              pagination
              paginationPageSize={100}
              paginationPageSizeSelector={[20, 50, 100]}
              suppressAggFuncInHeader
              animateRows
              onGridReady={(params) => params.api.sizeColumnsToFit()}
              onFirstDataRendered={(params) => params.api.sizeColumnsToFit()}
              statusBar={{
                statusPanels: [
                  { statusPanel: "agTotalRowCountComponent", align: "left" },
                  { statusPanel: "agAggregationComponent", align: "right" },
                ],
              }}
            />
          </div>
        )}
      </Box>
    </Box>
  );
}

DmaPayoutsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  data: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
};
